import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandMoreRounded, FileDownloadRounded } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AcademicAnalyticsResult } from '@dosfilos/application';
import { graduationCandidatesApi } from '../../../../lib/api/graduation-candidates.api';
import { callable } from '../../../../lib/callable';
import { errorText } from '../../../../lib/error-text';
import { useTenantStore } from '../../../../stores/tenant.store';
import { GAP_BUCKETS, type GapBucket } from './analytics-charts.model';
import {
  displayName,
  gapList,
  listCsv,
  nearList,
  peopleRequests,
  repeatList,
  type PersonName,
} from './named-lists.model';

// ─── Panel académico: las tres listas con nombres (solo gestión) ────────────
// Los nombres se piden SOLO al abrir una lista (getAnalyticsPeople, por partes
// si pasan el tope), nunca al cargar el panel.

const getAnalyticsPeople = callable<
  { tenantId: string; personIds: string[] },
  { people: PersonName[] }
>('getAnalyticsPeople');

/** Los nombres de unos ids, pedidos por partes. Solo corre con `enabled`. */
export function usePeopleNames(ids: readonly string[], enabled: boolean) {
  const { currentTenantId } = useTenantStore();
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ['analytics-people', currentTenantId, key],
    enabled: enabled && !!currentTenantId && key.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const map = new Map<string, PersonName>();
      for (const part of peopleRequests(key)) {
        const res = await getAnalyticsPeople({ tenantId: currentTenantId!, personIds: part });
        for (const p of res.data.people) map.set(p.id, p);
      }
      return map;
    },
  });
}

function downloadCsv(csv: string, file: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${file}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Una tabla de personas: total, CSV y filas en orden alfabético. */
function ListTable({
  header,
  rows,
  file,
  loading,
  error,
}: {
  header: string[];
  rows: string[][];
  file: string;
  loading?: boolean;
  error?: unknown;
}) {
  const { t } = useTranslation('sis');
  if (error) return <Alert severity="error">{errorText(error)}</Alert>;
  if (loading) return <CircularProgress size={22} />;
  return (
    <Stack gap={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        <Typography variant="body2" color="text.secondary">
          {t('reports.analytics.lists.total', { count: rows.length })}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadRounded />}
          disabled={rows.length === 0}
          onClick={() => downloadCsv(listCsv(header, rows), file)}
        >
          {t('reports.candidates.exportCsv')}
        </Button>
      </Stack>
      {rows.length > 0 && (
        <TableContainer sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {header.map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} hover>
                  {row.map((c, j) => (
                    <TableCell key={j}>{c}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

/** a) Cerca de egresar y sin inscripción reciente: una celda de la tabla d. */
export function NearListDialog({
  cell,
  onClose,
  r,
  near,
}: {
  cell: { missing: number; gap: GapBucket } | null;
  onClose: () => void;
  r: AcademicAnalyticsResult;
  near: number;
}) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const L = (k: string, o?: Record<string, unknown>) => t(`reports.analytics.lists.${k}`, o ?? {});
  // La respuesta completa (con nombres) se pide recién al abrir una celda.
  const full = useQuery({
    queryKey: ['graduation-candidates', currentTenantId],
    enabled: !!cell && !!currentTenantId,
    retry: false,
    queryFn: () => graduationCandidatesApi.list({ tenantId: currentTenantId! }),
  });
  const rows =
    cell && full.data ? nearList(full.data.candidates, r.lastEnrollment.buckets, cell, near) : [];
  const which = (x: (typeof rows)[number]) =>
    [
      x.requiredMissing.length ? x.requiredMissing.join(', ') : '',
      x.groupShortfall ? L('near.core', { count: x.groupShortfall }) : '',
    ]
      .filter(Boolean)
      .join(' · ') || '—';
  return (
    <Dialog open={!!cell} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {L('near.title')}
        {cell && (
          <Typography variant="body2" color="text.secondary">
            {L('near.cell', {
              missing:
                cell.missing === 0
                  ? t('reports.analytics.candidates.zero')
                  : t('reports.analytics.candidates.row', { count: cell.missing }),
              gap: t(`reports.analytics.candidates.gaps.${cell.gap}`),
            })}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <ListTable
          header={[
            L('cols.name'),
            L('cols.code'),
            L('cols.missing'),
            L('cols.which'),
            L('cols.lastTerm'),
          ]}
          rows={rows.map((x) => [
            displayName(x),
            x.studentCode ?? '—',
            String(x.coursesMissing),
            which(x),
            x.lastTerm ?? '—',
          ])}
          file={L('near.file')}
          loading={full.isLoading}
          error={full.error}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{L('close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function GapAccordion({ r, bucket }: { r: AcademicAnalyticsResult; bucket: GapBucket }) {
  const { t } = useTranslation('sis');
  const L = (k: string, o?: Record<string, unknown>) => t(`reports.analytics.lists.${k}`, o ?? {});
  const [open, setOpen] = useState(false);
  const ids = r.lastEnrollment.buckets[bucket] ?? [];
  const names = usePeopleNames(ids, open);
  const rows = names.data ? gapList(r, bucket, names.data) : [];
  return (
    <Accordion disableGutters expanded={open} onChange={(_, v) => setOpen(v)} variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Typography variant="body2" fontWeight={600}>
          {`${t(`reports.analytics.candidates.gaps.${bucket}`)} · ${L('total', { count: ids.length })}`}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {open && (
          <ListTable
            header={[L('cols.name'), L('cols.code'), L('cols.lastTerm')]}
            rows={rows.map((x) => [displayName(x), x.studentCode ?? '—', x.lastTerm ?? '—'])}
            file={L('gap.file', { bucket })}
            loading={names.isLoading}
            error={names.error}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/** b) Tiempo desde la última inscripción: un desplegable por tramo. */
export function GapLists({ r }: { r: AcademicAnalyticsResult }) {
  const { t } = useTranslation('sis');
  return (
    <Stack gap={1} sx={{ mt: 3 }}>
      <Typography variant="subtitle2">{t('reports.analytics.lists.gap.title')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('reports.analytics.lists.gap.definition')}
      </Typography>
      {GAP_BUCKETS.map((b) => (
        <GapAccordion key={b} r={r} bucket={b} />
      ))}
    </Stack>
  );
}

/** c) Repitencia, debajo del gráfico de notas, filtrable por curso. */
export function RepeatList({
  r,
  courses,
}: {
  r: AcademicAnalyticsResult;
  courses: ReadonlyMap<string, { code: string; name: string }>;
}) {
  const { t } = useTranslation('sis');
  const L = (k: string, o?: Record<string, unknown>) => t(`reports.analytics.lists.${k}`, o ?? {});
  const [open, setOpen] = useState(false);
  const [courseId, setCourseId] = useState<string | null>(null);
  const names = usePeopleNames(
    r.repeaters.map((x) => x.studentId),
    open,
  );
  const rows = names.data ? repeatList(r, names.data, courses, courseId) : [];
  const courseIds = [...new Set(r.repeaters.map((x) => x.courseId))].sort((a, b) =>
    (courses.get(a)?.code ?? a).localeCompare(courses.get(b)?.code ?? b, undefined, {
      numeric: true,
    }),
  );
  return (
    <Accordion
      disableGutters
      expanded={open}
      onChange={(_, v) => setOpen(v)}
      variant="outlined"
      sx={{ mt: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Typography variant="body2" fontWeight={600}>
          {`${L('repeat.title')} · ${L('total', { count: r.repeaters.length })}`}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={1.5}>
          <Typography variant="caption" color="text.secondary">
            {L('repeat.definition')}
          </Typography>
          <Box>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="an-repeat-course">{L('repeat.course')}</InputLabel>
              <Select
                labelId="an-repeat-course"
                label={L('repeat.course')}
                value={courseId ?? '__all__'}
                onChange={(e) => setCourseId(e.target.value === '__all__' ? null : e.target.value)}
              >
                <MenuItem value="__all__">{L('repeat.allCourses')}</MenuItem>
                {courseIds.map((id) => (
                  <MenuItem key={id} value={id}>
                    {`${courses.get(id)?.code ?? id} · ${courses.get(id)?.name ?? ''}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {open && (
            <ListTable
              header={[L('cols.name'), L('cols.code'), L('cols.course'), L('cols.attempts')]}
              rows={rows.map((x) => [
                displayName(x),
                x.studentCode ?? '—',
                x.courseCode,
                String(x.attempts),
              ])}
              file={L('repeat.file')}
              loading={names.isLoading}
              error={names.error}
            />
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
